import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Activity, Users, Clock, TrendingUp, BarChart3, Eye, Calendar, Utensils, Apple, Crown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [mealSwapStats, setMealSwapStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30);

  useEffect(() => {
    // Check if user is admin
    if (user && user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }

    if (user) {
      fetchAnalytics();
    }
  }, [user, dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // Fetch both general analytics and meal swap analytics
      const [generalResponse, mealSwapResponse] = await Promise.all([
        axios.get(`${API}/analytics/stats?days=${dateRange}`, { withCredentials: true }),
        axios.get(`${API}/analytics/meal-swaps?days=${dateRange}`, { withCredentials: true })
      ]);
      
      setStats(generalResponse.data);
      setMealSwapStats(mealSwapResponse.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0 seconds';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins === 0) return `${secs} seconds`;
    return `${mins}m ${secs}s`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div className="max-w-7xl mx-auto pt-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="max-w-7xl mx-auto pt-8 pb-20">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <BarChart3 className="h-8 w-8 mr-3 text-indigo-600" />
                Analytics Dashboard
              </h1>
              <p className="text-gray-600 mt-2">Admin-only insights and user behavior metrics</p>
            </div>

            {/* Date Range Selector */}
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-gray-500" />
              <select
                value={dateRange}
                onChange={(e) => setDateRange(Number(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </div>
          </div>
        </div>

        {/* Overview Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* Total Events */}
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardDescription>Total Events</CardDescription>
              <CardTitle className="text-3xl font-bold flex items-center">
                <Activity className="h-8 w-8 mr-2 text-blue-500" />
                {stats?.total_events || 0}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                All tracked user interactions
              </p>
            </CardContent>
          </Card>

          {/* Total Sessions */}
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-3">
              <CardDescription>Total Sessions</CardDescription>
              <CardTitle className="text-3xl font-bold flex items-center">
                <TrendingUp className="h-8 w-8 mr-2 text-green-500" />
                {stats?.total_sessions || 0}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Unique user sessions
              </p>
            </CardContent>
          </Card>

          {/* Total Users */}
          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="pb-3">
              <CardDescription>Active Users</CardDescription>
              <CardTitle className="text-3xl font-bold flex items-center">
                <Users className="h-8 w-8 mr-2 text-purple-500" />
                {stats?.total_users || 0}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Unique users in period
              </p>
            </CardContent>
          </Card>

          {/* Avg Session Duration */}
          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="pb-3">
              <CardDescription>Avg Session Time</CardDescription>
              <CardTitle className="text-3xl font-bold flex items-center">
                <Clock className="h-8 w-8 mr-2 text-orange-500" />
                {Math.round(stats?.avg_session_duration || 0)}s
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                {formatDuration(stats?.avg_session_duration || 0)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Meal Swap Analytics Section */}
        {mealSwapStats && (
          <>
            <div className="mb-4 mt-8">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                <Utensils className="h-7 w-7 mr-3 text-purple-600" />
                Meal Swap Analytics
              </h2>
              <p className="text-gray-600 mt-1">Freemium conversion and swap pattern insights</p>
            </div>

            {/* Meal Swap Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {/* Total Swaps */}
              <Card className="border-l-4 border-l-purple-500">
                <CardHeader className="pb-3">
                  <CardDescription>Total Meal Swaps</CardDescription>
                  <CardTitle className="text-3xl font-bold flex items-center">
                    <Utensils className="h-8 w-8 mr-2 text-purple-500" />
                    {mealSwapStats.total_swaps || 0}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">
                    {mealSwapStats.total_users || 0} users swapping meals
                  </p>
                </CardContent>
              </Card>

              {/* Free vs Premium */}
              <Card className="border-l-4 border-l-blue-500">
                <CardHeader className="pb-3">
                  <CardDescription>User Split</CardDescription>
                  <CardTitle className="text-2xl font-bold flex items-center gap-2">
                    <Users className="h-7 w-7 text-blue-500" />
                    <span className="text-green-600">{mealSwapStats.premium_users}</span>
                    <span className="text-gray-400">/</span>
                    <span className="text-gray-600">{mealSwapStats.free_users}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">
                    Premium / Free users
                  </p>
                </CardContent>
              </Card>

              {/* Avg Swaps Per User */}
              <Card className="border-l-4 border-l-green-500">
                <CardHeader className="pb-3">
                  <CardDescription>Avg Swaps/User</CardDescription>
                  <CardTitle className="text-3xl font-bold flex items-center">
                    <TrendingUp className="h-8 w-8 mr-2 text-green-500" />
                    {mealSwapStats.avg_swaps_per_user || 0}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">
                    Average engagement level
                  </p>
                </CardContent>
              </Card>

              {/* Conversion Rate */}
              <Card className="border-l-4 border-l-amber-500">
                <CardHeader className="pb-3">
                  <CardDescription>Free → Premium</CardDescription>
                  <CardTitle className="text-3xl font-bold flex items-center">
                    <Crown className="h-8 w-8 mr-2 text-amber-500" />
                    {mealSwapStats.conversion_rate || 0}%
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">
                    Conversion rate to premium
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Cuisine Preferences & Popular Swaps */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Cuisine Preferences */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Apple className="h-5 w-5 mr-2 text-purple-600" />
                    Cuisine Preferences
                  </CardTitle>
                  <CardDescription>Most popular cuisines from swaps</CardDescription>
                </CardHeader>
                <CardContent>
                  {mealSwapStats.cuisine_preferences && mealSwapStats.cuisine_preferences.length > 0 ? (
                    <div className="space-y-3">
                      {mealSwapStats.cuisine_preferences.map((cuisine, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <Badge className="bg-purple-100 text-purple-700 w-8 h-8 rounded-full flex items-center justify-center">
                              {index + 1}
                            </Badge>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 capitalize">{cuisine.cuisine?.replace(/_/g, ' ') || 'Unknown'}</p>
                              <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                <div
                                  className="bg-purple-600 h-2 rounded-full"
                                  style={{
                                    width: `${(cuisine.swap_count / (mealSwapStats.cuisine_preferences[0]?.swap_count || 1)) * 100}%`
                                  }}
                                ></div>
                              </div>
                            </div>
                          </div>
                          <Badge variant="secondary" className="ml-3">
                            {cuisine.swap_count} swaps
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No cuisine data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Popular Swaps */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
                    Popular Meal Swaps
                  </CardTitle>
                  <CardDescription>Most common meal substitutions</CardDescription>
                </CardHeader>
                <CardContent>
                  {mealSwapStats.popular_swaps && mealSwapStats.popular_swaps.length > 0 ? (
                    <div className="space-y-3">
                      {mealSwapStats.popular_swaps.slice(0, 5).map((swap, index) => (
                        <div key={index} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <Badge className="bg-green-100 text-green-700">
                              #{index + 1}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {swap.swap_count} swaps
                            </Badge>
                          </div>
                          <div className="text-sm">
                            <p className="text-gray-600">
                              <span className="line-through">{swap.from_meal || 'Unknown'}</span>
                            </p>
                            <p className="font-medium text-gray-900">
                              → {swap.to_meal || 'Unknown'}
                            </p>
                            {swap.cuisine && (
                              <Badge variant="secondary" className="mt-1 text-xs capitalize">
                                {swap.cuisine.replace(/_/g, ' ')}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No swap data yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* Most Used Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <TrendingUp className="h-5 w-5 mr-2 text-indigo-600" />
                Most Used Features
              </CardTitle>
              <CardDescription>Top features by user engagement</CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.most_used_features && stats.most_used_features.length > 0 ? (
                <div className="space-y-3">
                  {stats.most_used_features.slice(0, 10).map((feature, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <Badge className="bg-indigo-100 text-indigo-700 w-8 h-8 rounded-full flex items-center justify-center">
                          {index + 1}
                        </Badge>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{feature.feature}</p>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                            <div
                              className="bg-indigo-600 h-2 rounded-full"
                              style={{
                                width: `${(feature.usage_count / (stats.most_used_features[0]?.usage_count || 1)) * 100}%`
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                      <Badge variant="secondary" className="ml-3">
                        {feature.usage_count} uses
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No feature usage data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Most Viewed Pages */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Eye className="h-5 w-5 mr-2 text-green-600" />
                Most Viewed Pages
              </CardTitle>
              <CardDescription>Top pages by view count</CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.most_viewed_pages && stats.most_viewed_pages.length > 0 ? (
                <div className="space-y-3">
                  {stats.most_viewed_pages.slice(0, 10).map((page, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <Badge className="bg-green-100 text-green-700 w-8 h-8 rounded-full flex items-center justify-center">
                          {index + 1}
                        </Badge>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{page.page}</p>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                            <div
                              className="bg-green-600 h-2 rounded-full"
                              style={{
                                width: `${(page.views / (stats.most_viewed_pages[0]?.views || 1)) * 100}%`
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                      <Badge variant="secondary" className="ml-3">
                        {page.views} views
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No page view data yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Activity className="h-5 w-5 mr-2 text-blue-600" />
              Recent Activity
            </CardTitle>
            <CardDescription>Last 20 user events</CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.recent_activity && stats.recent_activity.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">User</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Event</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Page</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Type</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_activity.map((activity, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-gray-900">
                          {activity.user_email || 'Unknown'}
                        </td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900">
                          {activity.event_name}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {activity.page || 'N/A'}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="text-xs">
                            {activity.event_type}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500">
                          {formatDate(activity.timestamp)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No recent activity</p>
            )}
          </CardContent>
        </Card>

        {/* Info Banner */}
        <Card className="mt-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Activity className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-900">Analytics Data Collection</p>
                <p className="text-sm text-blue-700 mt-1">
                  This dashboard shows aggregated user behavior data. All analytics are collected securely 
                  with 30-second batching, 30-minute session timeouts, and no PII in metadata. Data helps 
                  improve personalization and UX decisions.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminAnalytics;
